/**
 * Normalisert importkontrakt for fremtidige kilder (PLS for knuseverket, VisionLink-telematikk for maskiner).
 * Kun forberedelse: feltdefinisjoner, enheter, tidssone, identitet, kildeangivelse, dekning, eksempelfiler,
 * mapping-forhåndsvisning, validering og definert duplikathåndtering. Ingen leverandørendepunkt kalles.
 */
import Papa from 'papaparse';
import type { LineState, Quality } from '../domain/types';
import { hoursBetween, isIsoUtc } from '../domain/time';

export interface ContractField {
  column: string;
  required: boolean;
  type: 'string' | 'number' | 'datetime_utc' | 'enum' | 'integer';
  unit?: string;
  normalizedField: string;
  description: string;
  allowed?: readonly string[];
}

export interface ImportContract {
  id: 'visionlink' | 'pls';
  sourceId: 'maskindata' | 'produksjon';
  title: string;
  systemLabel: string;
  identity: string;
  duplicateRule: string;
  timeRule: string;
  coverageRule: string;
  exampleFile: string;
  fields: ContractField[];
}

export const QUALITIES: readonly Quality[] = ['measured', 'estimated', 'missing', 'imported'] as const;
export const LINE_STATES: readonly LineState[] = ['production', 'stop', 'planned_off'] as const;
export const RECORD_TYPES = ['production', 'line_state'] as const;
export const MACHINE_SOURCES = ['visionlink', 'manual', 'other'] as const;
export const ACTIVITIES = ['production', 'loading_out', 'service'] as const;

const COMMON_TIME = 'ISO 8601 i UTC med Z (f.eks. 2026-10-02T05:00:00Z). Halvåpent intervall [from_utc, to_utc). Døgn og perioder grupperes i Europe/Oslo av portalen – kilden skal ikke levere lokal tid.';

export const VISIONLINK_CONTRACT: ImportContract = {
  id: 'visionlink',
  sourceId: 'maskindata',
  title: 'Maskindata (VisionLink / telematikk)',
  systemLabel: 'Cat VisionLink eller tilsvarende telematikk-eksport',
  identity: 'record_id + revision per maskin (machine_id). Samme record_id med høyere revision erstatter tidligere bidrag; lik revision = uendret; lavere revision ignoreres.',
  duplicateRule: 'Duplikat record_id + revision i samme fil avviser hele filen. Nye poster som overlapper eksisterende intervall for samme maskin varsles (begge inngår til de er avklart).',
  timeRule: COMMON_TIME,
  coverageRule: 'Dekning telles per forventet maskinintervall i produksjonsområdet. Tom celle = null (status «missing»); målt 0 er gyldig verdi. Manglende diesel gjør L/t, estimert CO₂ og kostanslag utilgjengelig – aldri 0.',
  exampleFile: '/maler/visionlink-eksempel.csv',
  fields: [
    { column: 'record_id', required: true, type: 'string', normalizedField: 'sourceRecordId', description: 'Kildens stabile post-ID for intervallet' },
    { column: 'machine_id', required: true, type: 'string', normalizedField: 'machineId', description: 'Registrert maskin-ID i portalen (maskinregisteret er master)' },
    { column: 'from_utc', required: true, type: 'datetime_utc', normalizedField: 'fromUtc', description: 'Intervallstart (UTC, Z)' },
    { column: 'to_utc', required: true, type: 'datetime_utc', normalizedField: 'toUtc', description: 'Intervallslutt (UTC, Z), etter from_utc' },
    { column: 'engine_hours', required: true, type: 'number', unit: 'h', normalizedField: 'engineHours', description: 'Motortimer i intervallet (≤ intervallets lengde). Tom = mangler' },
    { column: 'idle_hours', required: true, type: 'number', unit: 'h', normalizedField: 'idleHours', description: 'Tomgangstimer (inngår i motortimer, ≤ engine_hours). Tom = mangler' },
    { column: 'fuel_litres', required: true, type: 'number', unit: 'L', normalizedField: 'fuelLitres', description: 'Dieselforbruk i intervallet. Tom = mangler' },
    { column: 'reported_co2_kg', required: false, type: 'number', unit: 'kg', normalizedField: 'reportedCo2Kg', description: 'CO₂ rapportert direkte av telematikk. Når feltet finnes brukes det foran estimat fra diesel – aldri begge' },
    { column: 'quality', required: true, type: 'enum', normalizedField: 'quality', allowed: QUALITIES, description: 'measured | estimated | missing | imported' },
    { column: 'revision', required: false, type: 'integer', normalizedField: 'revision', description: 'Heltall ≥ 1, standard 1. Høyere revisjon erstatter' },
    { column: 'activity', required: false, type: 'enum', normalizedField: 'activity', allowed: ACTIVITIES, description: 'Aktivitet dersom kilden kjenner den (ellers brukes aktivitetsallokeringen i portalen)' },
    { column: 'source', required: false, type: 'enum', normalizedField: 'sourceSystem', allowed: MACHINE_SOURCES, description: 'Kildesystem: visionlink | manual | other (standard visionlink)' },
    { column: 'hours_unit', required: false, type: 'enum', normalizedField: '-', allowed: ['h'], description: 'Enhetskontroll: må være «h»' },
    { column: 'fuel_unit', required: false, type: 'enum', normalizedField: '-', allowed: ['L'], description: 'Enhetskontroll: må være «L»' },
  ],
};

export const PLS_CONTRACT: ImportContract = {
  id: 'pls',
  sourceId: 'produksjon',
  title: 'Produksjon og linjetilstand (PLS knuseverk)',
  systemLabel: 'Eksport fra knuseverkets PLS/SCADA (historikk eller API – må verifiseres i pilot)',
  identity: 'record_id + revision per linje (line_id). Produksjonsposter (record_type=production) og linjetilstand (record_type=line_state) er to posttyper i samme kontrakt.',
  duplicateRule: 'Duplikat record_id + revision avviser hele filen. Overlappende produksjonsposter for samme linje og fraksjon varsles. Produsert tonn er alltid fra denne kilden – lastet/levert mengde (SmartDok) legges aldri til.',
  timeRule: COMMON_TIME,
  coverageRule: 'Dekning telles per planlagt produksjonsdag: dager med produksjonsposter og dager med linjetilstand. Mangler linjetilstand for en dag vises produksjonstid/stopptid som «ikke målt», aldri 0 h.',
  exampleFile: '/maler/pls-eksempel.csv',
  fields: [
    { column: 'record_id', required: true, type: 'string', normalizedField: 'sourceRecordId', description: 'Kildens stabile post-ID' },
    { column: 'line_id', required: true, type: 'string', normalizedField: 'lineId', description: 'Produksjonslinje (f.eks. linje-1)' },
    { column: 'record_type', required: true, type: 'enum', normalizedField: '-', allowed: RECORD_TYPES, description: 'production (tonn per fraksjon) eller line_state (produksjon/stopp)' },
    { column: 'from_utc', required: true, type: 'datetime_utc', normalizedField: 'fromUtc', description: 'Start (UTC, Z)' },
    { column: 'to_utc', required: true, type: 'datetime_utc', normalizedField: 'toUtc', description: 'Slutt (UTC, Z), etter from_utc' },
    { column: 'fraction_id', required: false, type: 'string', normalizedField: 'fractionId', description: 'Påkrevd for production; tom for line_state' },
    { column: 'produced_tonnes', required: false, type: 'number', unit: 't', normalizedField: 'producedTonnes', description: 'Produsert mengde for production-poster. Tom = mangler (ikke 0)' },
    { column: 'state', required: false, type: 'enum', normalizedField: 'state', allowed: LINE_STATES, description: 'Påkrevd for line_state: production | stop | planned_off' },
    { column: 'stop_reason', required: false, type: 'string', normalizedField: 'stopReason', description: 'Registrert stoppårsak (kun line_state med state=stop)' },
    { column: 'quality', required: true, type: 'enum', normalizedField: 'quality', allowed: QUALITIES, description: 'measured | estimated | missing | imported' },
    { column: 'revision', required: false, type: 'integer', normalizedField: 'revision', description: 'Heltall ≥ 1, standard 1' },
  ],
};

export const IMPORT_CONTRACTS = [PLS_CONTRACT, VISIONLINK_CONTRACT];

export interface PlsRowError {
  row: number;
  column: string;
  value: string;
  reason: string;
}

export interface PlsParsedRow {
  row: number;
  recordId: string;
  lineId: string;
  recordType: (typeof RECORD_TYPES)[number];
  fromUtc: string;
  toUtc: string;
  fractionId: string | null;
  producedTonnes: number | null;
  state: LineState | null;
  stopReason: string | null;
  quality: Quality;
  revision: number;
}

export interface PlsParseResult {
  ok: boolean;
  rows: PlsParsedRow[];
  errors: PlsRowError[];
  headerErrors: string[];
  warnings: string[];
  rowCount: number;
  delimiter: string;
  /** Hvilke kolonner i filen som ble gjenkjent og hvilket normalisert felt de går til. */
  mapping: { column: string; normalizedField: string; status: 'ok' | 'ukjent' | 'mangler' }[];
  summary: { production: number; lineState: number; tonnes: number; productionHours: number; stopHours: number; days: string[] };
}

function num(raw: string | undefined): { value: number | null; error?: string } {
  const s = (raw ?? '').trim();
  if (s === '') return { value: null };
  if (/[a-zA-Z]/.test(s)) return { value: null, error: 'Verdien inneholder bokstaver/enhet. Oppgi tall uten enhet.' };
  const v = Number(s.replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(v)) return { value: null, error: 'Ikke et gyldig tall.' };
  return { value: v };
}

/** Mapping-forhåndsvisning: filens kolonner mot kontraktens normaliserte felt. */
export function mapColumns(contract: ImportContract, fileColumns: string[]): PlsParseResult['mapping'] {
  const out: PlsParseResult['mapping'] = [];
  for (const f of contract.fields) {
    const present = fileColumns.includes(f.column);
    if (present) out.push({ column: f.column, normalizedField: f.normalizedField, status: 'ok' });
    else if (f.required) out.push({ column: f.column, normalizedField: f.normalizedField, status: 'mangler' });
  }
  for (const c of fileColumns) if (!contract.fields.some((f) => f.column === c)) out.push({ column: c, normalizedField: '–', status: 'ukjent' });
  return out;
}

/**
 * Tørrkjøring av PLS-kontrakten: validerer, normaliserer og oppsummerer – uten å endre datasettet.
 * Hele filen avvises ved feil (ingen delvis mutasjon), identisk med maskinimporten.
 */
export function parseProductionCsv(text: string, knownLineIds: string[], knownFractionIds: string[]): PlsParseResult {
  const clean = text.replace(/^\uFEFF/, '');
  const parsed = Papa.parse<Record<string, string>>(clean, { header: true, skipEmptyLines: 'greedy', delimiter: '', transformHeader: (h) => h.trim().toLowerCase() });
  const fields = parsed.meta.fields ?? [];
  const mapping = mapColumns(PLS_CONTRACT, fields);
  const headerErrors = mapping.filter((m) => m.status === 'mangler').map((m) => `Mangler obligatorisk kolonne «${m.column}»`).concat(mapping.filter((m) => m.status === 'ukjent').map((m) => `Ukjent kolonne «${m.column}»`));
  const data = parsed.data;
  if (data.length > 10000) headerErrors.push(`Filen har ${data.length} rader; maks 10000 rader er tillatt.`);
  const empty: PlsParseResult['summary'] = { production: 0, lineState: 0, tonnes: 0, productionHours: 0, stopHours: 0, days: [] };
  if (headerErrors.length) return { ok: false, rows: [], errors: [], headerErrors, warnings: [], rowCount: data.length, delimiter: parsed.meta.delimiter, mapping, summary: empty };

  const errors: PlsRowError[] = [];
  const warnings: string[] = [];
  const rows: PlsParsedRow[] = [];
  const seen = new Map<string, number>();
  const days = new Set<string>();
  const summary = { ...empty };
  data.forEach((r, idx) => {
    const rowNo = idx + 2;
    const err = (column: string, value: string | undefined, reason: string) => errors.push({ row: rowNo, column, value: value ?? '', reason });
    const recordId = (r.record_id ?? '').trim();
    if (!recordId) err('record_id', r.record_id, 'Tom record_id');
    const lineId = (r.line_id ?? '').trim();
    if (!knownLineIds.includes(lineId)) err('line_id', lineId, `Ukjent linje (kjente: ${knownLineIds.join(', ')})`);
    const recordType = (r.record_type ?? '').trim() as PlsParsedRow['recordType'];
    if (!RECORD_TYPES.includes(recordType)) err('record_type', r.record_type, `Må være en av: ${RECORD_TYPES.join(', ')}`);
    const fromUtc = (r.from_utc ?? '').trim();
    const toUtc = (r.to_utc ?? '').trim();
    if (!isIsoUtc(fromUtc)) err('from_utc', fromUtc, 'Må være ISO 8601 i UTC med Z (f.eks. 2026-10-02T05:00:00Z) – ikke lokal tid');
    if (!isIsoUtc(toUtc)) err('to_utc', toUtc, 'Må være ISO 8601 i UTC med Z');
    let len = 0;
    if (isIsoUtc(fromUtc) && isIsoUtc(toUtc)) {
      len = hoursBetween(fromUtc, toUtc);
      if (!(len > 0)) err('to_utc', toUtc, 'to_utc må være etter from_utc (halvåpent intervall)');
      else days.add(fromUtc.slice(0, 10));
    }
    const fractionId = (r.fraction_id ?? '').trim() || null;
    const t = num(r.produced_tonnes);
    if (t.error) err('produced_tonnes', r.produced_tonnes, t.error);
    if (t.value !== null && t.value < 0) err('produced_tonnes', r.produced_tonnes, 'Negativ verdi er ikke tillatt');
    const stateRaw = (r.state ?? '').trim();
    const state = (stateRaw || null) as LineState | null;
    const stopReason = (r.stop_reason ?? '').trim() || null;
    const quality = (r.quality ?? '').trim() as Quality;
    if (!QUALITIES.includes(quality)) err('quality', r.quality, `Må være en av: ${QUALITIES.join(', ')}`);
    if (recordType === 'production') {
      if (!fractionId) err('fraction_id', r.fraction_id, 'Påkrevd for record_type=production');
      else if (!knownFractionIds.includes(fractionId)) err('fraction_id', fractionId, `Ukjent fraksjon (kjente: ${knownFractionIds.join(', ')})`);
      if (t.value === null && quality !== 'missing') err('produced_tonnes', r.produced_tonnes, 'Tom mengde krever quality=missing (mangler er ikke 0)');
      if (state) err('state', stateRaw, 'Skal være tom for production-poster');
      if (!errors.some((e) => e.row === rowNo)) {
        summary.production++;
        summary.tonnes += t.value ?? 0;
      }
    } else if (recordType === 'line_state') {
      if (!state || !LINE_STATES.includes(state)) err('state', stateRaw, `Påkrevd for line_state: ${LINE_STATES.join(', ')}`);
      if (t.value !== null) err('produced_tonnes', r.produced_tonnes, 'Skal være tom for line_state-poster (tonn kommer fra production-poster)');
      if (stopReason && state !== 'stop') err('stop_reason', stopReason, 'Stoppårsak kun for state=stop');
      if (!errors.some((e) => e.row === rowNo)) {
        summary.lineState++;
        if (state === 'production') summary.productionHours += len;
        else if (state === 'stop') summary.stopHours += len;
      }
    }
    let revision = 1;
    if (r.revision !== undefined && r.revision.trim() !== '') {
      const rv = Number(r.revision.trim());
      if (!Number.isInteger(rv) || rv < 1) err('revision', r.revision, 'Revisjon må være et heltall ≥ 1');
      else revision = rv;
    }
    const key = `${recordId}:${revision}`;
    if (recordId && seen.has(key)) err('record_id', recordId, `Duplikat av record_id + revisjon (også på rad ${seen.get(key)})`);
    seen.set(key, rowNo);
    rows.push({ row: rowNo, recordId, lineId, recordType, fromUtc, toUtc, fractionId, producedTonnes: t.value, state, stopReason, quality, revision });
  });

  // Overlapp mellom produksjonsposter for samme linje og fraksjon (varsel, ikke feil)
  const prod = rows.filter((x) => x.recordType === 'production' && x.fractionId);
  for (let i = 0; i < prod.length; i++) {
    for (let j = i + 1; j < prod.length; j++) {
      const a = prod[i], b = prod[j];
      if (a.lineId === b.lineId && a.fractionId === b.fractionId && a.fromUtc < b.toUtc && b.fromUtc < a.toUtc) {
        warnings.push(`Rad ${a.row} og ${b.row}: overlappende produksjonsposter for ${a.lineId}/${a.fractionId} – kontroller om det er duplikat.`);
      }
    }
  }
  summary.days = [...days].sort();
  return { ok: errors.length === 0, rows, errors, headerErrors, warnings, rowCount: data.length, delimiter: parsed.meta.delimiter, mapping, summary };
}
